import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection, query, where, orderBy, limit, startAfter, getDocs, getCountFromServer,
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { tokeniseName, soundex } from '../lib/phoneSearch/contactSearchIndex.js';
import { expandNameToken } from '../lib/phoneSearch/nicknameAliases.js';
import { phoneSearchTerm } from '../lib/phoneSearch/phoneTerm.js';
import {
  PHONE_CONTACTS, PAGE_SIZE, contactFromPhone, isListedPhoneContact,
} from '../lib/phoneContacts.js';

// The phone's contacts, searched the way the phone searches them
// (apps/voip/src/hooks/useContacts.js fetchContacts, copied): no search term
// pages the list 50 at a time by name; a term runs the phone's probes in
// parallel (name start, company start, every typed word, nicknames,
// sound-alike names, an exact phone number), merges them, keeps only rows
// that match every word, ranks them and shows the top 50. Every probe uses an
// index the phone already has. One failed probe never takes the search down:
// it reports `error` so "no matches" and "search failed" read differently.

const COUNT_KEY = 'eshot:phoneContactCount';
const COUNT_TTL_MS = 5 * 60 * 1000;

function readCachedCount() {
  try {
    const { count, ts } = JSON.parse(sessionStorage.getItem(COUNT_KEY) || 'null') || {};
    return typeof count === 'number' && Date.now() - ts < COUNT_TTL_MS ? count : null;
  } catch { return null; }
}

export async function fetchPhoneContacts(term, cursor, { contactType } = {}) {
  const trimmed = (term || '').trim().toLowerCase();
  const coll = collection(db, PHONE_CONTACTS);
  let probeFailed = false;
  const safeGetDocs = async (q) => {
    try { return await getDocs(q); } catch { probeFailed = true; return { docs: [] }; }
  };
  const toRow = (d) => ({ id: d.id, data: d.data() });

  if (trimmed) {
    const searchTokens = tokeniseName(trimmed);
    const phoneticCodes = [...new Set(searchTokens.map(soundex).filter(Boolean))].slice(0, 10);
    const multiToken = searchTokens.length > 1;
    const tokenFetch = multiToken ? 120 : PAGE_SIZE;
    const aliasFetch = multiToken ? 30 : 8;
    const seenProbe = new Set();
    const tokenQs = [];
    const addProbe = (field, tok, lim) => {
      const k = `${field}:${tok}`;
      if (seenProbe.has(k)) return;
      seenProbe.add(k);
      tokenQs.push(query(coll, where(field, 'array-contains', tok), orderBy('_nameLower', 'asc'), limit(lim)));
    };
    for (const st of searchTokens) {
      addProbe('_nameTokens', st, tokenFetch);
      for (const al of expandNameToken(st)) if (al !== st) addProbe('_nameWords', al, aliasFetch);
    }
    tokenQs.length = Math.min(tokenQs.length, 12);
    const nameQ = query(coll, where('_nameLower', '>=', trimmed), where('_nameLower', '<=', `${trimmed}`), orderBy('_nameLower', 'asc'), limit(PAGE_SIZE));
    const companyQ = query(coll, where('_companyNormalisedLower', '>=', trimmed), where('_companyNormalisedLower', '<=', `${trimmed}`), orderBy('_companyNormalisedLower', 'asc'), limit(PAGE_SIZE));
    const phoneticQ = phoneticCodes.length
      ? query(coll, where('_namePhonetics', 'array-contains-any', phoneticCodes), limit(PAGE_SIZE * 2))
      : null;
    const phoneTerm = phoneSearchTerm(term);
    const phoneQ = phoneTerm ? query(coll, where('_phones', 'array-contains', phoneTerm), limit(PAGE_SIZE)) : null;

    const [nameSnap, companySnap, phoneticSnap, phoneSnap, ...tokenSnaps] = await Promise.all([
      safeGetDocs(nameQ),
      safeGetDocs(companyQ),
      phoneticQ ? safeGetDocs(phoneticQ) : { docs: [] },
      phoneQ ? safeGetDocs(phoneQ) : { docs: [] },
      ...tokenQs.map((q) => safeGetDocs(q)),
    ]);
    const phoneMatchIds = new Set(phoneSnap.docs.map((d) => d.id));
    const byId = new Map();
    for (const d of [...nameSnap.docs, ...companySnap.docs, ...tokenSnaps.flatMap((s) => s.docs), ...phoneticSnap.docs, ...phoneSnap.docs]) {
      if (!byId.has(d.id)) byId.set(d.id, toRow(d));
    }
    const matchesAllTokens = ({ id, data }) => {
      if (phoneMatchIds.has(id)) return true;
      const nameTokens = tokeniseName(data.name || '');
      return searchTokens.every((st) => {
        if (nameTokens.some((nt) => nt.startsWith(st))) return true;
        const aliases = expandNameToken(st);
        if (aliases.length > 1 && nameTokens.some((nt) => aliases.includes(nt))) return true;
        const sx = soundex(st);
        if (sx && nameTokens.some((nt) => soundex(nt) === sx)) return true;
        return (data._companyNormalisedLower || '').startsWith(trimmed);
      });
    };
    const rank = ({ id, data }) => {
      if (phoneMatchIds.has(id)) return -1;
      if ((data._nameLower || '').startsWith(trimmed)) return 0;
      const nameTokens = tokeniseName(data.name || '');
      if (searchTokens.every((st) => {
        const aliases = expandNameToken(st);
        return nameTokens.some((nt) => nt.startsWith(st) || aliases.includes(nt));
      })) return 1;
      if ((data._companyNormalisedLower || '').startsWith(trimmed)) return 2;
      return 3;
    };
    const rows = [...byId.values()]
      .filter((r) => isListedPhoneContact(r.data))
      .filter((r) => !contactType || (r.data.contactType || 'unknown') === contactType)
      .filter(matchesAllTokens)
      .sort((a, b) => rank(a) - rank(b) || (a.data.name || '').localeCompare(b.data.name || ''))
      .slice(0, PAGE_SIZE);
    return { contacts: rows.map((r) => contactFromPhone(r.id, r.data)), last: null, more: false, error: probeFailed };
  }

  const constraints = [];
  if (contactType) constraints.push(where('contactType', '==', contactType));
  constraints.push(orderBy('name', 'asc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(PAGE_SIZE));
  const snap = await safeGetDocs(query(coll, ...constraints));
  const contacts = snap.docs.map(toRow).filter((r) => isListedPhoneContact(r.data)).map((r) => contactFromPhone(r.id, r.data));
  return {
    contacts,
    last: snap.docs[snap.docs.length - 1] || null,
    more: snap.docs.length === PAGE_SIZE,
    error: probeFailed,
  };
}

/**
 * How many records voip_contacts holds: one server count, cached for 5
 * minutes. It includes people the list hides (left, removed from Gold-Vision,
 * training), so it is shown as the phone's total, never as people e-shot lists.
 */
export function usePhoneContactCount() {
  const [count, setCount] = useState(readCachedCount);
  useEffect(() => {
    if (readCachedCount() !== null) return;
    getCountFromServer(collection(db, PHONE_CONTACTS))
      .then((snap) => {
        const n = snap.data().count;
        setCount(n);
        try { sessionStorage.setItem(COUNT_KEY, JSON.stringify({ count: n, ts: Date.now() })); } catch { /* ignore */ }
      })
      .catch(() => {});
  }, []);
  return count;
}

/**
 * The Contacts page's data: `search` (debounced by the caller) and an optional
 * phone contact type. `loadMore` pages on when there is no search term.
 */
export function usePhoneContacts(search, { contactType = null } = {}) {
  const [state, setState] = useState({ contacts: [], loading: true, loadingMore: false, hasMore: false, error: false });
  const totalCount = usePhoneContactCount();
  const cursorRef = useRef(null);
  const runRef = useRef(0);

  useEffect(() => {
    const run = ++runRef.current;
    cursorRef.current = null;
    setState((s) => ({ ...s, loading: true, error: false }));
    fetchPhoneContacts(search, null, { contactType })
      .then(({ contacts, last, more, error }) => {
        if (run !== runRef.current) return;
        cursorRef.current = last;
        // Always this fetch's rows, never a previous term's next to the new one.
        setState({ contacts, loading: false, loadingMore: false, hasMore: more, error });
      })
      .catch(() => {
        if (run !== runRef.current) return;
        setState({ contacts: [], loading: false, loadingMore: false, hasMore: false, error: true });
      });
  }, [search, contactType]);

  const loadMore = useCallback(async () => {
    if (!cursorRef.current || state.loadingMore || !state.hasMore) return;
    const run = runRef.current;
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      const { contacts, last, more, error } = await fetchPhoneContacts('', cursorRef.current, { contactType });
      if (run !== runRef.current) return;
      cursorRef.current = last;
      setState((s) => {
        const have = new Set(s.contacts.map((c) => c.id));
        return { ...s, contacts: [...s.contacts, ...contacts.filter((c) => !have.has(c.id))], loadingMore: false, hasMore: more, error: s.error || error };
      });
    } catch {
      if (run === runRef.current) setState((s) => ({ ...s, loadingMore: false, error: true }));
    }
  }, [contactType, state.loadingMore, state.hasMore]);

  return { ...state, totalCount, loadMore };
}
